import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import '../ViewReports/ViewReports.scss';
import Searchimg from '../../assets/images/search-icon.svg';
import { buildDeviceNameMap, formatDeviceNamesList } from '../../utils/reportDevices';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import useApi from '../../hook/useAPI';
import { DEVICE, REPORT } from '../../utils/const';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import moment from 'moment-timezone';

function ViewReports({ onSelectReport, selectedReportId }) {
  const { postData, getData, putData, deleteData } = useApi();
  const [deviceData, setDeviceData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [eventType, setEventType] = useState([]);
  const [deviceIds, setDeviceIds] = useState([]);
  const [originalDeviceData, setOriginalDeviceData] = useState([]);
  const [reportName, setReportName] = useState('');
  const [startDate1, setStartDate1] = useState('');
  const [endDate1, setEndDate1] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [allReports, setAllReports] = useState([]);
  const [allReportsOriginal, setAllReportsOriginal] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchReport, setSearchReport] = useState('');
  const [isLoadingReports, setIsLoadingReports] = useState(true);

  const countDaysBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end - start;
    return Math.abs(timeDifference / (1000 * 3600 * 24));
  };

  const loadReports = useCallback(async (selectFirst = false) => {
    setIsLoadingReports(true);
    try {
      const response = await getData(REPORT.GET_ALL_REPORTS, null, false);
      if (response?.status === 200) {
        const data = Array.isArray(response.data) ? response.data : [];
        setAllReports(data);
        setAllReportsOriginal(data);
        if (selectFirst && data[0]?._id) {
          onSelectReport(data[0]._id);
        }
        if (data.length === 0) {
          onSelectReport(null);
        }
      }
    } catch (error) {
      console.error('Error fetching all reports:', error);
      toast.error('Unable to load reports.');
    } finally {
      setIsLoadingReports(false);
    }
  }, [getData, onSelectReport]);

  const searchDevices = useCallback(debounce(async (query) => {
    try {
      const response = await getData(
        `${DEVICE?.GET_CURRENT_DEVICE_STATUS}?search=${query}`,
        null,
        false,
      );
      if (response.status === 200 && response?.data?.length > 0) {
        localStorage.setItem('deviceData', JSON.stringify(response.data));
        setDeviceData(response.data);
        setOriginalDeviceData(response.data);
      } else {
        setDeviceData([]);
        setOriginalDeviceData([]);
      }
    } catch (error) {
      console.error('Error fetching device details:', error);
    }
  }, 500), [getData]);

  useEffect(() => {
    loadReports(true);
  }, [loadReports]);

  useEffect(() => {
    searchDevices('');
    return () => searchDevices.cancel();
  }, [searchDevices]);

  useEffect(() => {
    if (!isModalOpen) {
      return undefined;
    }
    document.body.classList.add('report-modal-open');
    return () => {
      document.body.classList.remove('report-modal-open');
    };
  }, [isModalOpen]);

  const initialValues = isEdit ? {
    name: reportName,
    date: countDaysBetweenDates(startDate1, endDate1),
    startDate: moment(startDate1).format('YYYY-MM-DD'),
    endDate: moment(endDate1).format('YYYY-MM-DD'),
    eventType: eventType || [],
    deviceIds,
  } : {
    name: '',
    date: '',
    startDate: '',
    endDate: '',
    eventType: [],
    deviceIds: [],
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Report name is required'),
    startDate: Yup.string().required('Start date is required'),
    endDate: Yup.string().required('End date is required'),
    eventType: Yup.array().min(1, 'Select at least one event type'),
    deviceIds: Yup.array(),
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedId('');
    setEventType([]);
    setDeviceIds([]);
    setReportName('');
    setStartDate1('');
    setEndDate1('');
    setSearchQuery('');
  };

  const handleCreateReport = async (data) => {
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    const payload = {
      name: data.name,
      startDate,
      endDate,
      eventType: isEdit ? eventType : data.eventType,
      deviceIds: isEdit ? deviceIds : data.deviceIds,
      deviceName: data.deviceName,
    };

    const message = isEdit ? 'updated' : 'created';

    try {
      const response = isEdit
        ? await putData(`${REPORT.UPDATE_REPORT}/${selectedId}`, payload)
        : await postData(`${REPORT.POST_REPORT}`, payload);

      if (response?.status === 200 || response?.status === true) {
        await loadReports(false);
        if (!isEdit && response?.data?._id) {
          onSelectReport(response.data._id);
        }
        toast.success(`Report ${message} successfully`);
        closeModal();
      }
    } catch (error) {
      console.error(`Error in ${message} report:`, error);
      toast.error(`Failed to ${message} report. Please try again.`);
    }
  };

  const editReport = async (reportId, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!reportId) {
      return;
    }

    const response = await getData(`${REPORT.GET_REPORT}/${reportId}`, null, false);
    if (response?.status === 200) {
      const report = response?.data?.report;
      setStartDate1(report?.startDate?.split('T')[0] || '');
      setEndDate1(report?.endDate?.split('T')[0] || '');
      setEventType(report?.eventType || []);
      setDeviceIds(report?.deviceIds || []);
      setReportName(report?.name || '');
      setSelectedId(reportId);
      setIsEdit(true);
      setIsModalOpen(true);
    }
  };

  const deleteReport = async (reportId, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!reportId) {
      return;
    }
    if (!window.confirm('Delete this report? This cannot be undone.')) {
      return;
    }

    try {
      const response = await deleteData(`${REPORT.Delete_REPORTS}/${reportId}`);
      if (response?.status === 200) {
        toast.success(response?.message || 'Report deleted.');
        if (String(selectedReportId) === String(reportId)) {
          onSelectReport(null);
        }
        await loadReports(true);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Unable to delete report.');
    }
  };

  const handleSearchDevice = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filteredDevices = originalDeviceData.filter((device) => (
      device.name.toLowerCase().includes(query)
    ));
    setDeviceData(filteredDevices);
  };

  const handleSearchReport = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchReport(query);
    const filteredReports = allReportsOriginal.filter((report) => (
      report.name.toLowerCase().includes(query)
    ));
    setAllReports(filteredReports);
  };

  const formatDate2 = (date) => {
    const parsed = new Date(date);
    const month = parsed.getUTCMonth() + 1;
    return `${parsed.getUTCDate()}/${month}/${parsed.getUTCFullYear()}`;
  };

  const handleSelectReport = (reportId) => {
    onSelectReport(reportId);
  };

  const deviceNameById = useMemo(
    () => buildDeviceNameMap(originalDeviceData),
    [originalDeviceData],
  );

  const getReportDeviceLabel = (report) => {
    const names = (report?.deviceIds || [])
      .map((id) => deviceNameById[id] || deviceNameById[String(id)])
      .filter(Boolean);
    return formatDeviceNamesList(names, 2);
  };

  return (
    <>
      <div className={`card view-reports-panel h-100${isModalOpen ? ' view-reports-panel--behind-modal' : ''}`}>
        <div className="card-body">
          <div className="view-reports-toolbar">
            <div>
              <h2 className="view-reports-title">Saved reports</h2>
              <p className="view-reports-subtitle">Select a report to preview export data.</p>
            </div>
          </div>

          <div className="view-reports-actions">
            <label className="view-reports-search-label" htmlFor="view-reports-search-input">
              Search reports
            </label>
            <div className="search-box-wrap view-reports-search">
              <img className="search-box-icon" src={Searchimg} alt="" />
              <input
                id="view-reports-search-input"
                type="search"
                value={searchReport}
                placeholder="Search by report name"
                onChange={handleSearchReport}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary view-reports-create-btn"
              onClick={() => {
                setIsEdit(false);
                setIsModalOpen(true);
              }}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Create report
            </button>
          </div>

          <div className="view-reports-list" role="listbox" aria-label="Saved reports">
            {isLoadingReports && (
              <div className="view-reports-empty">Loading reports…</div>
            )}

            {!isLoadingReports && allReports.length === 0 && (
              <div className="view-reports-empty">
                <h4>No reports yet</h4>
                <p>Create your first report to track device status, trips, and zones.</p>
              </div>
            )}

            {!isLoadingReports && allReports.map((report) => {
              const isSelected = String(selectedReportId) === String(report._id);
              return (
                <div
                  key={report._id}
                  role="option"
                  aria-selected={isSelected}
                  className={`view-reports-list-item${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handleSelectReport(report._id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelectReport(report._id);
                    }
                  }}
                  tabIndex={0}
                >
                  <div className="view-reports-list-copy">
                    <h5 className="ellipsis-text">{report.name}</h5>
                    <p className="view-reports-list-date">{formatDate2(report.createdAt)}</p>
                    <p className="view-reports-list-devices">{getReportDeviceLabel(report)}</p>
                  </div>
                  <div className="view-reports-list-actions dropdown">
                    <button
                      type="button"
                      className="view-reports-menu-btn"
                      id={`report-menu-${report._id}`}
                      data-bs-toggle="dropdown"
                      data-bs-boundary="viewport"
                      aria-expanded="false"
                      aria-label={`Actions for ${report.name}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <i className="bi bi-three-dots" aria-hidden="true" />
                    </button>
                    <ul
                      className="dropdown-menu dropdown-menu-end view-reports-dropdown-menu"
                      aria-labelledby={`report-menu-${report._id}`}
                    >
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={(event) => editReport(report._id, event)}
                        >
                          <i className="bi bi-pencil-square" aria-hidden="true" />
                          Edit
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-danger"
                          onClick={(event) => deleteReport(report._id, event)}
                        >
                          <i className="bi bi-trash" aria-hidden="true" />
                          Delete
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isModalOpen && createPortal(
        <>
          <button
            type="button"
            className="modal-backdrop fade show view-reports-backdrop"
            aria-label="Close dialog"
            onClick={closeModal}
          />
          <div
            className="modal fade bs_common_modal create-new-report-modal show"
            id="exampleModal"
            role="dialog"
            tabIndex="-1"
            aria-labelledby="exampleModalLabel"
            aria-modal="true"
            style={{ display: 'block' }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-body">
                  <button
                    type="button"
                    className="btn-close report-modal-close"
                    aria-label="Close"
                    onClick={closeModal}
                  />
                  <div className="dialog-content-body report-modal-body">
                    <div className="report-modal-header">
                      <h4 className="dialog-title">{isEdit ? 'Update report' : 'Create report'}</h4>
                      <p className="report-modal-subtitle">
                        Name the report, choose a date range, event types, and devices.
                      </p>
                    </div>
                <Formik
                  initialValues={initialValues}
                  enableReinitialize
                  validationSchema={validationSchema}
                  onSubmit={(formData) => handleCreateReport(formData)}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    setFieldValue,
                  }) => {
                    const handleSelectAll = (e) => {
                      const isChecked = e.target.checked;
                      const allDeviceIds = isChecked ? deviceData.map((device) => device._id) : [];
                      if (isEdit) {
                        setDeviceIds(allDeviceIds);
                      } else {
                        setFieldValue('deviceIds', allDeviceIds);
                      }
                    };
                    const isAllSelected = isEdit
                      ? deviceData.length > 0 && deviceIds.length === deviceData.length
                      : deviceData.length > 0 && values.deviceIds.length === deviceData.length;

                    return (
                      <Form onSubmit={handleSubmit} className="report-modal-form">
                        <div className="add-device-form">
                          <div className="row g-3">
                            <div className="col-md-6">
                              <div className="form_field_wrapper report-modal-field">
                                <label htmlFor="report-name">Report name</label>
                                <Field
                                  id="report-name"
                                  name="name"
                                  type="text"
                                  className="form-control report-modal-input"
                                  placeholder="Enter report name"
                                />
                                {touched.name && errors.name && (
                                  <div className="text-danger small">{errors.name}</div>
                                )}
                              </div>
                              <div className="form_field_wrapper report-modal-field select-half">
                                <label>Report time period</label>
                                <div className="date-picker-container report-modal-date-range">
                                  <input
                                    type="date"
                                    name="startDate"
                                    value={values.startDate}
                                    className="form-control report-modal-input"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  />
                                  <span className="date-separator">to</span>
                                  <input
                                    type="date"
                                    name="endDate"
                                    value={values.endDate}
                                    className="form-control report-modal-input"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  />
                                </div>
                              </div>
                              <div className="form_field_wrapper report-modal-field">
                                <label>Event types</label>
                                <div className="text-field-card report-modal-option-card">
                                  <div className="device_listing mt-0 report-modal-option-list">
                                    {[
                                      { value: 'current', label: 'Current Status' },
                                      { value: 'trip', label: 'Trips' },
                                      { value: 'zones', label: 'Zones' },
                                    ].map((option) => (
                                      <div className="form-check mt-0" key={option.value}>
                                        <Field
                                          name="eventType"
                                          className="form-check-input"
                                          type="checkbox"
                                          value={option.value}
                                          id={`event-${option.value}`}
                                          checked={isEdit
                                            ? eventType.includes(option.value)
                                            : values.eventType.includes(option.value)}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            if (isEdit) {
                                              setEventType(isChecked
                                                ? [...eventType, option.value]
                                                : eventType.filter((item) => item !== option.value));
                                            } else {
                                              setFieldValue(
                                                'eventType',
                                                isChecked
                                                  ? [...values.eventType, option.value]
                                                  : values.eventType.filter((item) => item !== option.value),
                                              );
                                            }
                                          }}
                                        />
                                        <label className="form-check-label" htmlFor={`event-${option.value}`}>
                                          {option.label}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                {touched.eventType && errors.eventType && (
                                  <div className="text-danger small">{errors.eventType}</div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form_field_wrapper report-modal-field">
                                <label>Choose devices</label>
                                <div className="text-field-card cate-search-card report-modal-device-card report-modal-option-card">
                                  <div className="search-box-wrap report-modal-device-search">
                                    <img className="search-box-icon" src={Searchimg} alt="" />
                                    <input
                                      type="text"
                                      value={searchQuery}
                                      placeholder="Search device name"
                                      onChange={handleSearchDevice}
                                    />
                                  </div>
                                  <div className="device_listing report-modal-device-list">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="select-all-devices"
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                      />
                                      <label className="form-check-label" htmlFor="select-all-devices">
                                        Select all
                                      </label>
                                    </div>
                                    {deviceData?.map((device) => (
                                      <div className="form-check" key={device._id}>
                                        <Field
                                          name="deviceIds"
                                          className="form-check-input"
                                          type="checkbox"
                                          value={device._id}
                                          id={`device-${device._id}`}
                                          checked={isEdit
                                            ? deviceIds.includes(device._id)
                                            : values.deviceIds.includes(device._id)}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            if (isEdit) {
                                              setDeviceIds(isChecked
                                                ? [...deviceIds, device._id]
                                                : deviceIds.filter((id) => id !== device._id));
                                            } else {
                                              setFieldValue(
                                                'deviceIds',
                                                isChecked
                                                  ? [...values.deviceIds, device._id]
                                                  : values.deviceIds.filter((id) => id !== device._id),
                                              );
                                            }
                                          }}
                                        />
                                        <label className="form-check-label" htmlFor={`device-${device._id}`}>
                                          {device.name}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="report-modal-footer">
                            <button
                              type="button"
                              className="btn btn-light report-modal-cancel-btn"
                              onClick={closeModal}
                            >
                              Cancel
                            </button>
                            <button type="submit" className="btn btn-primary report-modal-submit-btn">
                              {isEdit ? 'Update report' : 'Generate report'}
                            </button>
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export default ViewReports;
